/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  dispatch,
  IAppState,
  IMediaListModel,
  IMediaSearchCategories,
  MediaStore,
  UrlHelper,
} from "@xala/common";
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { handleTypes } from "helpers/searchHelpers";
import { capitalize } from "helpers/textHelper";

import { ScrollContainer } from "./components/ScrollContainer";
import "./SearchTypesAndCategories.scss";

const mediaListId = MediaStore.Consts.MEDIA_LIST_SEARCH_KEY;

export const SearchTypesAndCategories = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const typeContainerRef = useRef<HTMLDivElement>(null);
  const categoryContainerRef = useRef<HTMLDivElement>(null);
  const categories = useSelector<IAppState, IMediaSearchCategories[]>(
    (state) => state.media?.mediaList?.[mediaListId]?.AllPagesCategories ?? [],
    shallowEqual
  );
  const mediaList = useSelector<IAppState, IMediaListModel>(
    (state) => state.media.mediaList[mediaListId] || {}
  );

  const types = useMemo(() => handleTypes(mediaList), [mediaList]);

  const orderedCategories = useMemo(() => {
    return categories.sort((a, b) => b.Count - a.Count);
  }, [categories]);

  const activeCategory = useMemo<number | null>(() => {
    const category = UrlHelper.parse(history.location.search).category;
    return parseInt(category, 10) || null;
  }, [history.location.search]);

  const handleCategoryChange = (categoryId?: number) => {
    dispatch(
      MediaStore.Actions.searchMedia({
        IncludeAllPagesTypes: false,
        IncludeAllPagesCategories: false,
        IncludeImages: true,
        Categories: categoryId ? [categoryId] : undefined,
        PageNumber: 1,
      })
    );

    history.push({
      search: UrlHelper.joinQueries(history.location.search, {
        category: categoryId,
        page: null,
      }),
    });
  };

  const activeType = useMemo<string>(() => {
    const type = UrlHelper.parse(history.location.search).type || "all";
    return type;
  }, [history.location.search]);

  const handleTypeChange = (type?: string | null, values?: string[] | null) => {
    dispatch(
      MediaStore.Actions.searchMedia({
        IncludeAllPagesTypes: false,
        IncludeAllPagesCategories: type !== "all",
        IncludeImages: true,
        Types: values || undefined,
        Categories: undefined,
        PageNumber: 1,
        ClearCategories: true,
      })
    );

    history.push({
      search: UrlHelper.joinQueries(history.location.search, {
        type: type,
        page: null,
        category: null,
      }),
    });
  };

  if (mediaList.TotalCount === 0) {
    return null;
  }

  return (
    <>
      {types[0].count > 0 && (
        <ScrollContainer
          ref={typeContainerRef}
          containerRef={typeContainerRef}
          activeElementKey={activeType}
          dataType="data-type"
        >
          {types.map(({ key, name, count, params }) =>
            count > 0 ? (
              <button
                key={key}
                className={`SearchTypesAndCategories__button ${
                  activeType === key
                    ? "SearchTypesAndCategories__button-active"
                    : ""
                }`}
                onClick={() => handleTypeChange(key, params)}
                data-type={key}
              >
                {capitalize(t(name))} ({count})
              </button>
            ) : null
          )}
        </ScrollContainer>
      )}
      {orderedCategories.length > 0 && (
        <ScrollContainer
          ref={categoryContainerRef}
          containerRef={categoryContainerRef}
          activeElementKey={activeCategory?.toString() ?? null}
          dataType="data-category"
        >
          {orderedCategories.map(({ CategoryId, CategoryName, Count }) => (
            <button
              key={CategoryId}
              className={`SearchTypesAndCategories__button ${
                activeCategory === CategoryId
                  ? "SearchTypesAndCategories__button-active"
                  : ""
              }`}
              onClick={() => handleCategoryChange(CategoryId)}
              data-category={CategoryId}
            >
              {capitalize(CategoryName)} ({Count})
            </button>
          ))}
        </ScrollContainer>
      )}
    </>
  );
};
